import {
  GET_COLUMNS,
  SET_COLUMN_SORT_INDEX,
  SET_COLUMN_SORT_ORDER,
} from '../../columns/columns.store';
import { GET_GROUPING } from '../../columns/grouping.store';
import { CLEAR_SORTING } from '../../columns/sorting.store';

import { createElement } from 'src/app/core/helpers/dom';
import { createHeaderCellTypeElement } from './utils/create-header-cell-type-element';
import { createHeaderRowElement } from './utils/create-header-row-element';
import { createHeaderSelectElement } from './utils/create-header-select-element';

import { checkElement } from '@core/helpers';
import { destroyTooltipsInSubtree } from 'src/app/project/features/tooltip/Tooltip';
import { debounce, TDebouncedFn } from 'src/app/project/shared/debounce';
import { EIconPath } from '../../../../../shared/enums/icons.enum';
import { TColumn } from '../../columns/column.model';
import { ESortOrder } from '../../sort-order-enum';
import Table from '../table/Table';
import { getSelectElementWidthPx } from '../table/utils/select-element-width';
import { getVisibleXBounds } from '../utils/get-visible-x-bounds';

export default class TableHead {
  private table: Table;
  minColumnWidth = 60;
  allSelected = false;
  resizeElement: HTMLElement = null;
  private debouncedReload: TDebouncedFn;
  rowElement: HTMLElement;
  element: HTMLElement = null;
  private cellsWrapper: HTMLElement = null;
  private lastReconciledFirstX: number | null = null;
  private lastReconciledLastX: number | null = null;

  constructor(parent: Table) {
    this.table = parent;
    this.rowElement = createHeaderRowElement();

    this.element = this.create();

    this.table.element.appendChild(checkElement(this.element));
    this.debouncedReload = debounce(() => this.table.reload({ reset: true }), 500);
  }

  private create(): HTMLElement {
    return createElement('div', {
      attrs: {
        class: 'table__head',
      },
      children: [this.rowElement],
    });
  }

  clear(): void {
    this.updateWidth(0);
  }

  load(keepScrollPosition: boolean = false): void {
    const scrollPosition = keepScrollPosition ? this.element.scrollLeft : 0;

    this.loadStructure();
    this.table.sortTable();

    this.element.scrollLeft = scrollPosition;
  }

  loadStructure(): void {
    this.updateWidth(this.table.width);
    this.invalidateReconcileCache();

    if (this.cellsWrapper) {
      destroyTooltipsInSubtree(this.cellsWrapper);
    }

    while (this.rowElement.firstChild) {
      this.rowElement.removeChild(this.rowElement.firstChild);
    }

    this.rowElement.appendChild(checkElement(createHeaderSelectElement()));

    this.cellsWrapper = createElement<HTMLElement>('div', {
      attrs: {
        class: 'table__head__row__cells',
        style: {
          position: 'relative',
          flexShrink: '0',
          height: '100%',
        },
      },
    });
    this.rowElement.appendChild(checkElement(this.cellsWrapper));
  }

  reconcile(): void {
    if (!this.cellsWrapper) {
      return;
    }

    const { firstX, lastX } = getVisibleXBounds(this.table);

    if (firstX === this.lastReconciledFirstX && lastX === this.lastReconciledLastX) {
      return;
    }

    this.lastReconciledFirstX = firstX;
    this.lastReconciledLastX = lastX;

    const visibleXIndexes = this.table.tableBody?.virtualScroller?.visibleXIndexes ?? [];

    while (this.cellsWrapper.firstChild) {
      const child = this.cellsWrapper.firstChild;

      if (child instanceof HTMLElement) {
        destroyTooltipsInSubtree(child);
      }

      this.cellsWrapper.removeChild(child);
    }

    this.cellsWrapper.style.width = `${this.getCellsWrapperWidth()}px`;

    const visibleColumnIndexes = this.table.visibleColumnIndexes ?? [];
    const visibleColumnPositions = this.table.visibleColumnPositions ?? [];
    const columns = GET_COLUMNS();

    visibleXIndexes.forEach((xIndex) => {
      const absoluteIndex = visibleColumnIndexes[xIndex];

      if (absoluteIndex === undefined) {
        return;
      }

      const column = columns[absoluteIndex];

      if (!column || column.hidden) {
        return;
      }

      const left = visibleColumnPositions[xIndex] ?? 0;
      const cellElement = createHeaderCellTypeElement(column);

      cellElement.style.position = 'absolute';
      cellElement.style.left = `${left}px`;
      cellElement.style.top = '0';
      cellElement.style.height = '100%';

      this.cellsWrapper.appendChild(checkElement(cellElement));
    });
  }

  private invalidateReconcileCache(): void {
    this.lastReconciledFirstX = null;
    this.lastReconciledLastX = null;
  }

  private getCellsWrapperWidth(): number {
    const columns = GET_COLUMNS();
    const grouping = GET_GROUPING();
    const sum = (this.table.visibleColumnIndexes ?? []).reduce(
      (total, absoluteIndex) => total + (columns[absoluteIndex]?.width ?? 0),
      0,
    );

    return sum + grouping.length * 16;
  }

  updateWidth(width: number = this.table.width): void {
    (this.element.children[0] as HTMLElement).style.width = `${width}px`;
  }

  scrollLeft(left: number): void {
    this.element.scrollLeft = left;
  }

  addColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  removeColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  moveColumn(): void {
    this.invalidateReconcileCache();
    this.reconcile();
  }

  sortColumn(sortedColumn: TColumn): void {
    const sortButton = this.table.siteOptionsService.getSortButton();
    const groupButton = this.table.siteOptionsService.getGroupButton();
    const oldSortOrder = sortedColumn.sortOrder;

    CLEAR_SORTING();

    GET_COLUMNS().forEach((column) => {
      delete column.sortIndex;
      delete column.sortOrder;
    });

    let sortOrder = null;

    if (!oldSortOrder) {
      sortOrder = ESortOrder.ASC;
    } else if (oldSortOrder === ESortOrder.ASC) {
      sortOrder = ESortOrder.DESC;
    }

    if (sortOrder) {
      SET_COLUMN_SORT_INDEX(sortedColumn.index, 0);
      SET_COLUMN_SORT_ORDER(sortedColumn.index, sortOrder);
    } else {
      SET_COLUMN_SORT_INDEX(sortedColumn.index);
      SET_COLUMN_SORT_ORDER(sortedColumn.index);
    }

    this.table.sortTable({ updatePreferences: true });

    sortButton.update({ closeDropdown: true });
    groupButton.update();

    this.debouncedReload();
  }

  updateFrozenColumnWidth(): void {
    const selectElement: HTMLElement = document.querySelector('.table__head__select');
    const rowElement: HTMLElement = document.querySelector('.table__head__row');
    const grouping = GET_GROUPING();

    if (selectElement) {
      if (grouping.length) {
        selectElement.style.width = getSelectElementWidthPx(grouping.length);
        // selectElement.style.marginLeft = `${16 * grouping.length}px`;
      } else {
        selectElement.style.width = getSelectElementWidthPx(grouping.length);
        // selectElement.style.marginLeft = '0';
      }
    }

    if (rowElement) {
      rowElement.style.marginLeft = '0px';
    }
  }

  // Points

  selectPoints(): void {
    const checkmark = this.element.children[0].children[0].children[0].children[0].children[0]
      .children[0] as HTMLImageElement;
    if (!this.allSelected) {
      checkmark.src = EIconPath.ICON_CHECHMARK_TABLE_SMALL;
      checkmark.classList.remove('customSiteTable__checkbox--indeterminate');
      checkmark.classList.add('customSiteTable__checkbox--selected');
    } else {
      this.table.hasSelectedAllPoints = true;
    }

    if (this.table.hasSelectedAllPoints) {
      checkmark.src = EIconPath.ICON_CHECHMARK_TABLE_SMALL;
    }
    if (this.table.isPointsSelectAllIndeterminate) {
      checkmark.src = EIconPath.ICON_CHECKMARK_HALF_SMALL;
      checkmark.classList.remove('customSiteTable__checkbox--selected');
      checkmark.classList.add('customSiteTable__checkbox--indeterminate');
    }

    this.allSelected = true;
  }

  deselectPoints(): void {
    const element = this.element.children[0].children[0].children[0].children[0].children[0]
      .children[0] as HTMLImageElement;

    if (this.allSelected) {
      element.src = EIconPath.ICON_CHECHMARK_TABLE_FALSE;

      element.classList.remove('customSiteTable__checkbox--selected');
    }

    if (this.table.selectedPointsCount > 0) {
      this.table.isPointsSelectAllIndeterminate = true;

      element.src = EIconPath.ICON_CHECKMARK_HALF_SMALL;
      element.classList.remove('customSiteTable__checkbox--selected');
      element.classList.add('customSiteTable__checkbox--indeterminate');
    } else {
      element.src = EIconPath.ICON_CHECHMARK_TABLE_FALSE;
      element.classList.remove(
        'customSiteTable__checkbox--selected',
        'customSiteTable__checkbox--indeterminate',
      );
      this.table.isPointsSelectAllIndeterminate = false;
    }
    this.allSelected = false;
  }

  destroy(): void {
    this.debouncedReload?.cancel();
  }
}
